import { css, keyframes } from '@emotion/react'

// 디자인 정본: englishflat-design /projects/graphics/earlybird-banner → -earlybird/Banner.tsx
// 보라 계열(그라데이션·글로우·강조색)은 얼리버드 홍보용이라 의도적으로 팔레트 밖 raw 값이다 — 토큰으로 바꾸지 말 것.

export const BANNER_H = 36
// 단계별 기준 폭 — 가장 넓은 칸을 담는 최소치. 고정폭이 아니라 max-width 로 걸어
// 가용 폭(1024 + 긴 프로필명이면 147px 실측) 안에서 줄어들게 한다. 1280 을 경계로 full/compact 를 가른다.
export const W_FULL = 192
export const W_COMPACT = 130

// 롤링 한 칸 높이 — keyframes 이동량과 1:1. 배너 높이와 같게 둬야 글자·타일이 잘린 채 구르지 않는다.
const ROLL_LINE = BANNER_H

// 배너 안 발광 타일 크기. 36 높이 배너에서는 이만큼이 한계.
const TILE = 20
// 구운 타일 그림의 실측(원본 상자 216 기준). 타일이 3D 로 기울어 있어 상자와 어긋난다 —
// dx/dy 는 상자 왼쪽·위에서 그림이 넘친 양(음수면 안쪽으로 들어온 것).
const TILE_ART = { box: 216, w: 195, h: 246, dx: -26.5, dy: 11.5 }

// 로고 락업 원본 비율(3073×558).
export const WM_RATIO = 3073 / 558

// '0원' 강조색 — 얼리버드 랜딩 띠배너와 같은 값(팔레트 밖, 의도된 것).
const OFFER_ACCENT = '#ece3ff'

// 세 칸 + 첫 칸 복제. 한 칸이 약 2.5초 머물고 0.5초에 걸쳐 넘어간다(9초에 세 칸).
// 마지막(복제) 칸에서 처음으로 되돌아갈 때 화면이 같아 되감김이 안 보인다.
const gnbRoll = keyframes`
  0%, 28% {
    transform: translateY(0);
  }
  33.3%, 61.3% {
    transform: translateY(-${ROLL_LINE}px);
  }
  66.6%, 94.6% {
    transform: translateY(-${ROLL_LINE * 2}px);
  }
  100% {
    transform: translateY(-${ROLL_LINE * 3}px);
  }
`

const S = {
  banner: css`
    position: relative;
    box-sizing: border-box;
    /* 기준 폭을 basis 로 걸고 shrink 를 허용한다 — 자리가 남으면 기준 폭, 모자라면(1024+긴 프로필명)
       그만큼 줄어든다. width:100% 는 콘텐츠 크기로 정해지는 부모와 순환이라 쓰지 않는다. */
    flex: 0 1 auto;
    min-width: 0;
    height: ${BANNER_H}px;
    padding: 0;
    /* 배너 ↔ 내신관 사이 숨통 — 배너는 남의 앱에 얹히는 손님이라 붙어 있으면 GNB 요소처럼 읽힌다. */
    margin-right: 16px;
    border: 0;
    border-radius: 8px;
    /* 배너 하나가 곧 창이다. 안쪽에 따로 상자를 두면 그 모서리에서 글로우가 잘려 배경 위에 네모난 경계가 생긴다. */
    overflow: hidden;
    color: var(--White);
    cursor: pointer;
    /* 배경 — 얼리버드 랜딩 띠배너보다 한 단계 밝다. 흰 GNB 에 얹히는 자리라 어두운 값은 무겁다. */
    background: radial-gradient(80% 270% at 28% 50%, #9a5cff 0%, #7433f0 48%, #4d18c4 100%);

    &[data-variant='full'] {
      width: ${W_FULL}px;
    }
    &[data-variant='compact'] {
      width: ${W_COMPACT}px;
    }

    /* 롤링 창 — 배너와 정확히 겹친다. 한 칸만 보이고 안쪽 목록이 위로 올라간다. */
    .gb-roll {
      position: absolute;
      inset: 0;
      display: block;
      overflow: hidden;
    }
    .gb-roll__list {
      display: block;
      animation: ${gnbRoll} 9s cubic-bezier(0.7, 0, 0.2, 1) infinite;
    }
    .gb-line {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      height: ${ROLL_LINE}px;
      padding: 0 12px;
      box-sizing: border-box;
      white-space: nowrap;
    }

    /* ① 이제 영어도 매쓰플랫처럼 — 타일 + 흰 글자. */
    .gb-text {
      font-size: 14px;
      line-height: ${ROLL_LINE}px;
      font-weight: 700;
      letter-spacing: -0.02em;
      /* '이제 영어도' 사이가 스페이스라 마진으로 못 늘린다. */
      word-spacing: 1px;
    }
    /* 발광 타일 — 랜딩의 3D 타일을 구워 둔 그림(tile.png · 80×80). CSS 로 20px 로 눌러 그리면
       옆면 겹의 경계가 반 픽셀에 걸려 자글거려, 크게 구운 그림을 브라우저가 줄이게 한다. */
    .gb-tile {
      position: relative;
      flex: none;
      display: block;
      width: ${TILE}px;
      height: ${TILE}px;
    }
    /* 그림은 상자보다 크다 — 넘치는 만큼 왼쪽·위로 당겨 상자와 타일 몸통을 겹친다. */
    .gb-tile img {
      position: absolute;
      left: ${(-TILE_ART.dx * TILE) / TILE_ART.box}px;
      top: ${(-TILE_ART.dy * TILE) / TILE_ART.box}px;
      width: ${(TILE_ART.w * TILE) / TILE_ART.box}px;
      height: ${(TILE_ART.h * TILE) / TILE_ART.box}px;
      max-width: none;
    }

    /* ② 지금 신청하면 모두 0원 — 랜딩 띠배너 offer 보다 한 단계 밝게(36px 배너에서는 그 값이 배경에 묻힌다). */
    .gb-offer .gb-text {
      font-weight: 600;
      letter-spacing: normal;
      word-spacing: normal;
      color: ${OFFER_ACCENT};
      text-shadow: 0 0 16px rgba(186, 150, 255, 0.35);
    }

    /* 강조 — 랜딩 띠배너 .hot 의 글로우(작은 글자라 겹 수만 줄인 값). */
    .gb-hot {
      font-style: normal;
      font-weight: 800;
      color: var(--White);
      text-shadow:
        0 0 4px rgba(255, 255, 255, 0.72),
        0 0 10px rgba(238, 224, 255, 0.58),
        0 0 24px rgba(196, 156, 255, 0.42);
    }

    /* ③ 로고 락업 — PNG 를 마스크로 써서 색을 currentColor(흰색)로 입힌다. */
    .gb-logo {
      display: block;
      filter: drop-shadow(0 0 6px rgba(226, 212, 255, 0.45));
    }
    .gb-logo__ink {
      display: block;
      width: 100%;
      height: 100%;
      background: currentColor;
      -webkit-mask-repeat: no-repeat;
      mask-repeat: no-repeat;
      -webkit-mask-position: 0 0;
      mask-position: 0 0;
    }

    /* 좁게는 한 단계 작게 — 130 폭에서 14px 는 말줄임된다. 9단계 스케일 밖(13px)이지만 홍보 에셋이라 예외. */
    &[data-variant='compact'] .gb-text {
      font-size: 13px;
    }
    /* 좁게는 타일을 뺀다 — 타일(20)+간격(6)이 글자 자리를 26 먹는다. 로고 칸이 정체를 전달한다. */
    &[data-variant='compact'] .gb-tile {
      display: none;
    }

    @media (prefers-reduced-motion: reduce) {
      .gb-roll__list {
        animation: none;
      }
    }
  `,
}

export default S
